/**
 * V5 关卡任务判定器 — 支持 5 种胜利条件
 *
 * v5-003 §1.3 + v5-004 §3: 教学关卡需要 reach_point / scan_enemy / survive_ticks
 * 等非战斗任务类型，引擎原先只支持 eliminate。
 *
 * 设计决策：
 *   - 纯函数，不依赖任何外部状态
 *   - 调用方（engine-scheduler）在每 tick 后调用 checkMission()
 *   - 返回值包含 win/fail 标记 + 判定原因
 */

import type { BattleState } from "./types";

// ============================================================
// 类型定义
// ============================================================

export type MissionType = "reach_point" | "eliminate" | "scan_enemy" | "survive_ticks" | "multi_phase";

export type FailConditionType = "timeout" | "unit_eliminated";

export interface WinCondition {
  type: MissionType;
  target?: [number, number];
  radius?: number;
}

export interface FailCondition {
  type: FailConditionType;
  tick?: number;       // timeout max tick
  unit?: "player" | "any";   // unit_eliminated: which team must survive
}

export interface MissionObjective {
  type: MissionType;
  params: {
    target?: [number, number];
    radius?: number;
    unit?: "player" | "enemy";
    tick?: number;
  };
  description: string;
}

export interface MissionConfig {
  missionId: string;
  objective: MissionObjective;
  winConditions: WinCondition[];
  failConditions: FailCondition[];
  hints: {
    onFail1?: string;
    onFail2?: string;
  };
}

export interface MissionResult {
  win: boolean;
  fail: boolean;
  reason: string;
  /** 复合条件下，当前已完成的子任务索引（multi_phase 用） */
  completedPhases?: number;
  /** 总子任务数（multi_phase 用） */
  totalPhases?: number;
  /** 是否首次 scan 到敌人 */
  scannedEnemy?: boolean;
}

// ============================================================
// 判定函数
// ============================================================

/**
 * 检查关卡任务是否完成或失败。
 *
 * @param state  当前战斗状态（来自引擎 tick 循环）
 * @param config 关卡任务配置
 * @returns MissionResult
 */
export function checkMission(state: BattleState, config: MissionConfig): MissionResult {
  const noFail: MissionResult = { win: false, fail: false, reason: "" };

  // --- 失败判定（优先级最高） ---
  for (const fc of config.failConditions) {
    const failResult = checkFailCondition(state, fc);
    if (failResult) return failResult;
  }

  // --- 胜利判定 ---
  for (const wc of config.winConditions) {
    const winResult = checkWinCondition(state, wc);
    if (winResult.win) return winResult;
  }

  return noFail;
}

// ============================================================
// 单条件判定
// ============================================================

function checkFailCondition(state: BattleState, fc: FailCondition): MissionResult | null {
  if (fc.type === "timeout" && fc.tick !== undefined) {
    if (state.tick >= fc.tick) {
      return { win: false, fail: true, reason: `演习超时（${fc.tick} ticks）` };
    }
  }

  if (fc.type === "unit_eliminated") {
    const playerAlive = isPlayerAlive(state);

    if (!playerAlive) {
      return { win: false, fail: true, reason: "Agent 已退出演习" };
    }
  }

  return null;
}

/** 检查玩家（team_a）是否有存活单位 */
function isPlayerAlive(state: BattleState): boolean {
  if (state.team_a?.tanks && state.team_a.tanks.length > 0) {
    return state.team_a.tanks.some((t) => t.is_alive);
  }
  // 单坦克 fallback
  return state.self.is_alive;
}

/** 检查蓝方（team_b）是否全部阵亡 */
function areAllEnemiesDead(state: BattleState): boolean {
  if (state.team_b?.tanks && state.team_b.tanks.length > 0) {
    return state.team_b.tanks.every((t) => !t.is_alive);
  }
  // 单坦克 fallback
  return !state.enemy.is_alive;
}

/** 检查是否有可见的蓝方单位 */
function isAnyEnemyVisible(state: BattleState): boolean {
  if (state.team_b?.tanks && state.team_b.tanks.length > 0) {
    return state.team_b.tanks.some((t) => t.is_visible === true);
  }
  // 单坦克 fallback
  return state.enemy.is_visible;
}

/** 获取玩家（team_a）所有坦克位置 */
function getPlayerPositions(state: BattleState): Array<{ x: number; y: number }> {
  if (state.team_a?.tanks && state.team_a.tanks.length > 0) {
    return state.team_a.tanks.filter((t) => t.is_alive).map((t) => t.position);
  }
  return [state.self.position];
}

function checkWinCondition(state: BattleState, wc: WinCondition): MissionResult {
  switch (wc.type) {
    case "reach_point":
      return checkReachPoint(state, wc);
    case "eliminate":
      return checkEliminate(state);
    case "scan_enemy":
      return checkScanEnemy(state);
    case "survive_ticks":
      return checkSurviveTicks(state, wc);
    case "multi_phase":
      return checkMultiPhase(state, wc);
    default:
      return { win: false, fail: false, reason: "" };
  }
}

// ============================================================
// 具体判定实现
// ============================================================

/** reach_point: 玩家到达目标坐标（切比雪夫距离 ≤ radius） */
function checkReachPoint(state: BattleState, wc: WinCondition): MissionResult {
  const target = wc.target;
  const radius = wc.radius ?? 1;
  if (!target) return { win: false, fail: false, reason: "" };

  const positions = getPlayerPositions(state);
  const anyInRange = positions.some((pos) => {
    const dx = Math.abs(pos.x - target[0]);
    const dy = Math.abs(pos.y - target[1]);
    return Math.max(dx, dy) <= radius;
  });

  if (anyInRange) {
    const tStr = `(${target[0]}, ${target[1]})`;
    return { win: true, fail: false, reason: `Agent 到达目标点 ${tStr}` };
  }

  return { win: false, fail: false, reason: "" };
}

/** eliminate: 敌方全部阵亡 */
function checkEliminate(state: BattleState): MissionResult {
  if (areAllEnemiesDead(state)) {
    return { win: true, fail: false, reason: "蓝方全部退出演习" };
  }

  return { win: false, fail: false, reason: "" };
}

/** scan_enemy: 首次 scan 发现敌人 */
function checkScanEnemy(state: BattleState): MissionResult {
  if (isAnyEnemyVisible(state)) {
    return {
      win: true,
      fail: false,
      reason: "Agent 成功发现蓝方位置",
      scannedEnemy: true,
    };
  }

  return { win: false, fail: false, reason: "" };
}

/** survive_ticks: 存活指定 tick 数 */
function checkSurviveTicks(state: BattleState, wc: WinCondition): MissionResult {
  // survive_ticks 由配置中的 target tick 决定
  // 由于 WinCondition 没有 ticks 字段，从 MissionObjective.params.tick 读取
  // 这里作为 reach_point 的变体：存活则通过
  // 实际判定中，survive_ticks 由 failConditions timeout 反向定义——不超时=存活成功
  if (wc.target && state.tick >= wc.target[0]) {
    return { win: true, fail: false, reason: `Agent 坚守了 ${state.tick} ticks` };
  }
  return { win: false, fail: false, reason: "" };
}

/** multi_phase: 复合任务（暂未实现，留接口） */
function checkMultiPhase(_state: BattleState, _wc: WinCondition): MissionResult {
  // P1: 复合任务判定器——按顺序检查子任务完成
  return { win: false, fail: false, reason: "" };
}

// ============================================================
// 工具函数：从 mission.json 构造 MissionConfig
// ============================================================

/**
 * 从 mission.json 原始数据构造 MissionConfig。
 * 用于屏 4 战斗初始化时加载任务配置。
 */
export function parseMissionConfig(raw: any): MissionConfig | null {
  if (!raw || typeof raw !== "object") return null;

  return {
    missionId: raw.missionId ?? "",
    objective: {
      type: raw.objective?.type ?? "eliminate",
      params: {
        target: raw.objective?.params?.target,
        radius: raw.objective?.params?.radius,
        unit: raw.objective?.params?.unit,
        tick: raw.objective?.params?.tick,
      },
      description: raw.objective?.description ?? "",
    },
    winConditions: Array.isArray(raw.winConditions) ? raw.winConditions : [],
    failConditions: Array.isArray(raw.failConditions) ? raw.failConditions : [
      { type: "timeout", tick: raw.maxTicks ?? 1800 },
      { type: "unit_eliminated", unit: "player" },
    ],
    hints: {
      onFail1: raw.hints?.onFail1,
      onFail2: raw.hints?.onFail2,
    },
  };
}

/**
 * 运行时加载关卡的 mission.json。
 *
 * @param levelId 关卡 ID
 * @returns MissionConfig，失败返回 null
 */
export async function loadMissionConfig(levelId: string): Promise<MissionConfig | null> {
  try {
    const url = `/data/levels/_builtin/${levelId}/mission.json`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const raw = await res.json();
    return parseMissionConfig(raw);
  } catch {
    console.warn(`[mission-judge] failed to load mission for ${levelId}`);
    return null;
  }
}
